import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { t } from '../../utils/translations';
import { getProducts, getProductCategories, getProductUnits } from '../../api/product';
import { Product, Category, Unit } from '../../types/product';
import { useNotifications } from '../../hooks/useNotifications';
import ProductEditPanel from '../Products/ProductEditPanel';

interface AddMultipleProductsDialogProps {
  open: boolean;
  onClose: () => void;
  onAddProducts: (productIds: string[]) => Promise<void>;
  usedProductIds?: string[];
}

const AddMultipleProductsDialog: React.FC<AddMultipleProductsDialogProps> = ({
  open,
  onClose,
  onAddProducts,
  usedProductIds = [],
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [productEditPanelOpen, setProductEditPanelOpen] = useState<boolean>(false);

  const { showError, showSuccess } = useNotifications();

  useEffect(() => {
    if (open) {
      fetchData();
      setSelectedProductIds(new Set());
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData, unitsData] = await Promise.all([
        getProducts(),
        getProductCategories(),
        getProductUnits(),
      ]);
      setProducts(productsData.filter((p) => p.status === 1));
      setCategories(categoriesData);
      setUnits(unitsData);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return products;
    }

    const query = searchQuery.toLowerCase();
    return products.filter((product) =>
      product.name.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  const handleToggleProduct = (productId: string) => {
    const newSelected = new Set(selectedProductIds);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProductIds(newSelected);
  };

  const handleSubmit = async () => {
    if (selectedProductIds.size === 0) {
      showError(t('shoppingLists.selectAtLeastOneProduct'), t('common.validationError'));
      return;
    }

    try {
      setSaving(true);
      await onAddProducts(Array.from(selectedProductIds));
      // Show success notification
      showSuccess(t('shoppingLists.productsAdded'), t('common.success'));
      // Clear selection after successful addition, but keep dialog open
      setSelectedProductIds(new Set());
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('shoppingLists.addProductsFailed'),
        t('common.error')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedProductIds(new Set());
    setSearchQuery('');
    setProductEditPanelOpen(false);
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleProductCreated = async () => {
    // Reload products list
    const productsData = await getProducts();
    const activeProducts = productsData.filter((p) => p.status === 1);
    setProducts(activeProducts);

    // Find the newest product by sorting by ID (assuming IDs are incrementing)
    const sortedByIdDesc = [...activeProducts].sort((a, b) => b.id.localeCompare(a.id));
    const newestProduct = sortedByIdDesc[0];

    // Select the newly created product
    if (newestProduct) {
      const newSelected = new Set(selectedProductIds);
      newSelected.add(newestProduct.id);
      setSelectedProductIds(newSelected);
    }

    // Close the product edit panel
    setProductEditPanelOpen(false);
  };

  const handleOpenProductEditPanel = () => {
    setProductEditPanelOpen(true);
  };

  const handleCloseProductEditPanel = () => {
    setProductEditPanelOpen(false);
  };

  return (
    <>
      <Drawer anchor="bottom" open={open} onClose={handleClose}>
        <Box sx={{ padding: 2, minHeight: '400px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', paddingBottom: 1}}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h6">{t('shoppingLists.addMultipleProductsTitle')}</Typography>
            <IconButton onClick={handleClose} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Search bar */}
              <TextField
                placeholder={t('shoppingLists.searchProducts')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                disabled={saving}
                sx={{ marginBottom: 2 }}
                InputProps={{
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClearSearch}
                        edge="end"
                        size="small"
                        aria-label={t('shoppingLists.clearSearch')}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Products list */}
              <Box sx={{ flex: 1, overflowY: 'auto', marginBottom: 2 }}>
                {filteredProducts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('shoppingLists.noProductsFound')}
                  </Typography>
                ) : (
                  <List>
                    {filteredProducts.map((product) => {
                      const isUsed = usedProductIds.includes(product.id);
                      const isSelected = selectedProductIds.has(product.id);

                      return (
                        <ListItem key={product.id} sx={{ padding: 0 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleProduct(product.id)}
                                disabled={isUsed || saving}
                              />
                            }
                            label={
                              <Typography
                                variant="body1"
                                sx={{
                                  color: isUsed ? 'text.disabled' : 'text.primary',
                                }}
                              >
                                {product.name} {isUsed && `(${t('shoppingLists.alreadyAdded')})`}
                              </Typography>
                            }
                            sx={{ width: '100%' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', marginTop: 1 }}>
                <Button onClick={handleClose} disabled={saving}>
                  {t('common.close')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={saving || selectedProductIds.size === 0}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                  {saving ? t('common.saving') : t('common.add')}
                </Button>
              </Box>

              <Divider sx={{ marginY: 1 }} />

              {/* Create new product button */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleOpenProductEditPanel}
                  disabled={saving}
                >
                  {t('products.createNewProduct')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      <ProductEditPanel
        open={productEditPanelOpen}
        product={null}
        categories={categories}
        units={units}
        onClose={handleCloseProductEditPanel}
        onUpdate={handleProductCreated}
      />
    </>
  );
};

export default AddMultipleProductsDialog;
